"use client";

import type { ToastTone } from "src/design-system";
import type { ToastType, ToastProps as LegacyToastProps } from "src/types/toast.types";

import { toast } from "sonner";

import { Toast } from "src/design-system";

import { APP_TOAST_DURATION_MS, APP_TOAST_ACTION_DURATION_MS } from "./toast-layout";

// ============================================================================
// Bridge sonner ↔ Design System `<Toast />`
// ============================================================================
//
// Histórico: a plataforma usava o renderer default do sonner com classNames
// custom em `<Snackbar />`. Trocamos pra `toast.custom(...)` renderizando o
// `<Toast />` do design system — assim toda a UI passa por um único
// componente reusável, com `IconBox`/`Card`/`Text`/`IconButton`.
//
// Sonner continua sendo a engine (portal, posicionamento, queueing, swipe
// pra dismiss, hotkeys). A diferença é só *o que* aparece dentro do card.
// ============================================================================

export const DEFAULT_APP_TOAST_OPTIONS = {
  duration: APP_TOAST_DURATION_MS,
};

type AppToastTone = ToastTone;

/** Mapeia `error` (legacy) → `danger` (DS). Mantém compat com `ToastType`. */
function toneFromLegacyType(type: ToastType | undefined): AppToastTone {
  switch (type) {
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "error":
      return "danger";
    case "info":
    default:
      return "info";
  }
}

export type AppToastOptions = {
  duration?: number;
  description?: string;
  action?: { label: string; onClick: () => void; icon?: string };
  /**
   * Ícone do toast (nome Iconify ou node). Sem isto, cai no glifo `linear` do
   * tom. Use quando o toast precisa pesar mais que um aviso de passagem — o
   * lançamento de um evento na súmula, por exemplo, que o organizador confere
   * de longe enquanto olha pro jogo.
   */
  icon?: string;
  /**
   * Barra de tempo escoando na base do card.
   *
   * **Desligada por padrão.** Ela só faz sentido quando existe algo a fazer
   * ANTES do toast sumir — na prática, o "Desfazer" do lance da súmula, em que
   * a barra é a contagem regressiva de uma janela real (o lance só vira
   * requisição quando ela acaba). Num aviso comum ela cronometrava a leitura de
   * uma frase que ninguém precisa terminar de ler, e a plataforma inteira ficou
   * com um relógio correndo no canto sem motivo.
   */
  showProgress?: boolean;
  /**
   * O toast fechou sozinho (o tempo acabou). O sonner pausa o tempo com o mouse em cima ou o dedo
   * na tela, então quem grava algo "quando o toast acabar" escuta aqui, e não num timer próprio,
   * que seguiria correndo com o toast pausado.
   */
  onAutoClose?: () => void;
  /** O toast foi fechado antes do tempo (pelo X, por arrastar ou pela própria ação). */
  onDismiss?: () => void;
};

/**
 * Núcleo — recebe título + tom + opções e dispara o sonner com `toast.custom`,
 * passando o `id` pro `<Toast />` para o botão de fechar funcionar.
 */
function emit(title: string, tone: AppToastTone, options: AppToastOptions = {}) {
  const { description, action, icon, showProgress = false, onAutoClose, onDismiss } = options;
  /**
   * Duração por natureza do toast:
   *  - erro NÃO expira: se o organizador olhou pro lado, perdeu a informação;
   *  - com ação, o dobro do tempo, porque existe algo a clicar;
   *  - o resto some sozinho.
   */
  const duration =
    options.duration ??
    (tone === "danger"
      ? Infinity
      : action
        ? APP_TOAST_ACTION_DURATION_MS
        : DEFAULT_APP_TOAST_OPTIONS.duration);

  /**
   * A ação do toast vale UM clique e fecha o toast.
   *
   * Sem isto, o "Desfazer" de um lance ficava na tela depois de usado e um
   * segundo clique desfazia o lance ANTERIOR — o organizador achava que estava
   * insistindo no mesmo botão e ia tirando gol por gol da súmula. A trava é por
   * toast (esta closure), não global: dois lances seguidos têm cada um o seu.
   */
  let actionUsed = false;
  /**
   * O sonner só chama o `onDismiss` no arrasto e no X dele. O nosso X e a ação
   * fecham com `toast.dismiss(id)`, que não avisa ninguém: quem gravava algo ao
   * fechar (a recusa do convite) ficava pendurado. Avisa uma vez só.
   */
  let dismissNotified = false;
  const notifyDismiss = () => {
    if (dismissNotified) return;
    dismissNotified = true;
    onDismiss?.();
  };

  return toast.custom(
    (id) => (
      <Toast
        tone={tone}
        title={title}
        description={description}
        action={
          action
            ? {
                ...action,
                onClick: () => {
                  if (actionUsed) return;
                  actionUsed = true;
                  action.onClick();
                  notifyDismiss();
                  toast.dismiss(id);
                },
              }
            : undefined
        }
        icon={icon}
        // Quando existe, a barra escoa na MESMA duração que o sonner usa para
        // fechar, então o desenho nunca mente sobre quanto ainda dá para agir.
        // Sem `showProgress` não há barra — ver `AppToastOptions`.
        progressMs={showProgress ? duration : undefined}
        onClose={() => {
          notifyDismiss();
          toast.dismiss(id);
        }}
      />
    ),
    { duration, onAutoClose, onDismiss: onDismiss ? notifyDismiss : undefined }
  );
}

// ============================================================================
// API pública — mantém os nomes existentes pra não quebrar call sites.
// ============================================================================

export function showAppToast({
  text,
  type,
  duration,
}: Pick<LegacyToastProps, "text" | "type" | "duration">): void {
  emit(text, toneFromLegacyType(type), { duration });
}

/** Retorna o id do toast — use com `dismissAppToast` quando a ação que ele
 *  anuncia for desfeita por outro caminho da tela. */
export function showAppSuccess(message: string, options?: AppToastOptions): string | number {
  return emit(message, "success", options);
}

export function showAppError(message: string, options?: AppToastOptions): void {
  emit(message, "danger", options);
}

/** Retorna o id do toast (útil pra `dismissAppToast(id)`, ex.: ação "Desfazer"). */
export function showAppInfo(message: string, options?: AppToastOptions): string | number {
  return emit(message, "info", options);
}

export function showAppWarning(message: string, options?: AppToastOptions): void {
  emit(message, "warning", options);
}

/** Sem `id` fecha todos os toasts; com `id` fecha só aquele. */
export function dismissAppToast(id?: string | number): void {
  toast.dismiss(id);
}

// ============================================================================
// Wrappers utilitários — substituem `toast.promise` mantendo a UX:
//   loading → success | error.
// ============================================================================

type PromiseMessages<T> = {
  loading: string;
  success: string | ((value: T) => string);
  error: string | ((err: unknown) => string);
};

/**
 * Equivalente ao `toast.promise` do sonner, mas usando nossos componentes.
 *
 * Implementação: mostra um toast `info` (loading), substitui por `success`
 * ou `danger` ao final. Como o `<Toast />` não tem spinner nativo, o estado
 * de loading vira um toast `info` com a string de loading — suficiente pros
 * casos atuais (troca de idioma, etc.).
 */
export function showAppPromise<T>(promise: Promise<T>, messages: PromiseMessages<T>): Promise<T> {
  const loadingId = emit(messages.loading, "info", { duration: Infinity });

  return promise
    .then((value) => {
      toast.dismiss(loadingId);
      const text = typeof messages.success === "function" ? messages.success(value) : messages.success;
      emit(text, "success");
      return value;
    })
    .catch((err) => {
      toast.dismiss(loadingId);
      const text = typeof messages.error === "function" ? messages.error(err) : messages.error;
      emit(text, "danger");
      throw err;
    });
}
